import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { GripVertical } from 'lucide-react';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { cn } from '@/lib/utils.ts';

type UseSortableReturn = ReturnType<typeof useSortable>;

type SortableItemContextValue = {
  attributes: UseSortableReturn['attributes'];
  listeners: UseSortableReturn['listeners'];
};

const SortableItemContext =
  React.createContext<SortableItemContextValue | null>(null);

type SortableListProps<T> = {
  items: T[] | undefined;
  getId: (item: T) => string;
  getOrderIndex: (item: T) => number;
  onReorder: (newIds: string[]) => void;
  children: (item: T) => React.ReactNode;
  className?: string;
};

function SortableList<T>({
  items,
  getId,
  getOrderIndex,
  onReorder,
  children,
  className,
}: SortableListProps<T>) {
  const serverOrderKey = React.useMemo(() => {
    if (!items) return '[]';
    return JSON.stringify(
      [...items].sort((a, b) => getOrderIndex(a) - getOrderIndex(b)).map(getId),
    );
  }, [items, getId, getOrderIndex]);

  const [orderedIds, setOrderedIds] = React.useState<string[]>([]);

  React.useEffect(() => {
    setOrderedIds(JSON.parse(serverOrderKey));
  }, [serverOrderKey]);

  const orderedItems = React.useMemo(() => {
    if (!items) return [];
    const byId = new Map(items.map((item) => [getId(item), item]));
    return orderedIds
      .map((id) => byId.get(id))
      .filter((item): item is T => item !== undefined);
  }, [orderedIds, items, getId]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      return;
    }
    const oldIndex = orderedIds.indexOf(active.id as string);
    const newIndex = orderedIds.indexOf(over.id as string);
    if (oldIndex === -1 || newIndex === -1) {
      return;
    }
    const newIds = arrayMove(orderedIds, oldIndex, newIndex);
    setOrderedIds(newIds);
    onReorder(newIds);
  };

  if (!items) return null;

  return (
    <div className={className}>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={orderedIds}
          strategy={verticalListSortingStrategy}
        >
          {orderedItems.map((item) => (
            <React.Fragment key={getId(item)}>{children(item)}</React.Fragment>
          ))}
        </SortableContext>
      </DndContext>
    </div>
  );
}

type SortableListItemProps = {
  id: string;
  asChild?: boolean;
  className?: string;
  children: React.ReactNode;
};

const SortableListItem = ({
  id,
  asChild,
  className,
  children,
}: SortableListItemProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : undefined,
  };

  const Comp = asChild ? Slot : 'div';

  return (
    <SortableItemContext.Provider value={{ attributes, listeners }}>
      <Comp ref={setNodeRef} style={style} className={className}>
        {children}
      </Comp>
    </SortableItemContext.Provider>
  );
};

type SortableListDragHandleProps = {
  className?: string;
  title?: string;
  ariaLabel?: string;
};

const SortableListDragHandle = ({
  className,
  title = 'Przeciągnij, aby zmienić kolejność',
  ariaLabel = 'Przeciągnij, aby zmienić kolejność',
}: SortableListDragHandleProps) => {
  const ctx = React.useContext(SortableItemContext);
  if (!ctx) {
    throw new Error(
      'SortableListDragHandle must be used inside <SortableListItem>',
    );
  }
  return (
    <button
      type="button"
      {...ctx.attributes}
      {...ctx.listeners}
      title={title}
      aria-label={ariaLabel}
      className={cn(
        'flex h-7 w-7 cursor-grab items-center justify-center rounded-sm text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700 active:cursor-grabbing',
        className,
      )}
    >
      <GripVertical className="size-4" />
    </button>
  );
};

export { SortableList, SortableListItem, SortableListDragHandle };
